import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';

import { useAllExpenses, useExpenseAggregates } from 'core/hooks';
import { KpiCard, KpiGrid, ChartCard } from 'molecules';

import { ExpensesTable } from '../../FakturowniaExpenses/ExpensesTable';
import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface IPayoutsTabProps {
  teacherId: string;
}

const fmt = (n: number) => n.toFixed(2);

/**
 * Teacher payouts (TZ §4.3). Phase 3 swap: the by-month chart now reads from
 * `expenseAggregates(teacherId, groupBy:'month')` — no more client-side
 * bucket-loop, no more `perPage=500` pull just to make a histogram. The
 * table below stays on `useAllExpenses` (Phase 9.1: auto-paginated, no
 * silent first-page slice) because it shows row-level detail.
 */
export const PayoutsTab = memo(({ teacherId }: IPayoutsTabProps) => {
  const { t } = useTranslation();

  const periodFrom = useMemo(
    () => moment().startOf('month').subtract(11, 'months').format('YYYY-MM-DD'),
    [],
  );
  const periodTo = useMemo(
    () => moment().endOf('month').format('YYYY-MM-DD'),
    [],
  );
  const months = useMemo(() => {
    const out: string[] = [];
    const start = moment().startOf('month').subtract(11, 'months');
    for (let i = 0; i < 12; i += 1) {
      out.push(start.clone().add(i, 'months').format('YYYY-MM'));
    }
    return out;
  }, []);

  const { rows: monthBuckets } = useExpenseAggregates(
    periodFrom,
    periodTo,
    'month',
    { teacherId },
  );
  const { data, fetching } = useAllExpenses({ teacherId });

  // Backend may skip empty months; align to the 12-month axis so the bar
  // chart renders gaps as zeros, not as missing categories. Phase 4: read
  // the native `total` field (== PLN for this team's data) instead of the
  // null-prone BYN-equivalent.
  const byMonth = useMemo(() => {
    const map = new Map<string, number>();
    monthBuckets.forEach((b) => {
      if (b.group) map.set(b.group, b.total || 0);
    });
    return months.map((m) => map.get(m) || 0);
  }, [monthBuckets, months]);

  const last12ByMonth = byMonth.reduce((s, n) => s + n, 0);
  const totalPln = data.reduce((s, r) => s + (r.amount || 0), 0);
  const ytdPln = useMemo(() => {
    const yearStart = moment().startOf('year');
    return data
      .filter((r) => r.date && moment(r.date).isSameOrAfter(yearStart))
      .reduce((s, r) => s + (r.amount || 0), 0);
  }, [data]);

  const chartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    }),
    [],
  );

  return (
    <S.DimWhenFetching fetching={fetching}>
      <div style={{ marginTop: 16 }}>
        <KpiGrid columns={3}>
          <KpiCard
            label={t('pages.teacherDetail.payouts.totalAllTime')}
            value={`${fmt(totalPln)} PLN`}
            subLabel={t('pages.teacherDetail.payouts.lastNRows', {
              count: data.length,
            })}
          />
          <KpiCard
            label={t('pages.teacherDetail.payouts.ytd')}
            value={`${fmt(ytdPln)} PLN`}
          />
          <KpiCard
            label={t('pages.teacherDetail.payouts.last12Months')}
            value={`${fmt(last12ByMonth)} PLN`}
          />
        </KpiGrid>
      </div>

      <ChartCard
        title={t('pages.teacherDetail.payouts.chartTitle')}
        meta={t('pages.teacherDetail.payouts.chartMeta')}
      >
        <Bar
          options={chartOptions}
          data={{
            labels: months,
            datasets: [
              {
                label: 'PLN',
                data: byMonth,
                backgroundColor: '#2563eb',
                borderRadius: 4,
              },
            ],
          }}
        />
      </ChartCard>

      {data.length === 0 ? (
        <S.EmptyState>{t('pages.teacherDetail.payouts.empty')}</S.EmptyState>
      ) : (
        <ExpensesTable rows={data} onRowClick={() => undefined} />
      )}
    </S.DimWhenFetching>
  );
});
